import React from "react";
import MuiTooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { format, parseISO } from "date-fns";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  addLocalDays,
  formatDate,
  formatDateReadable,
} from "../../../requests/utilities/helpers";
import AutoScheduleDailyView, {
  calculateScheduleGradeScore,
  calcScheduleLetter,
  getScheduleGradeColor,
} from "./AutoScheduleDailyView";
import AutoScheduleWeeklyView from "./AutoScheduleWeeklyView";
import ScheduleComplianceComparison, { ComplianceCount, ComparisonMetric } from "./ScheduleComplianceComparison";
import { changedScheduleRows, removedScheduleRows } from "./scheduleComparisonChanges";
import ExplicitRoleCoverage from "./ExplicitRoleCoverage";

const isNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value);

const PRIMARY_LABOR_COLOR = "#6d28d9";

const toNumber = (value) => {
  if (isNumber(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const getMemberId = (shift) => {
  if (typeof shift?.store_team_member === "object") {
    return shift.store_team_member?.id;
  }
  if (shift?.store_team_member !== undefined) {
    return shift.store_team_member;
  }
  return shift?.store_team_member_id;
};

const getShiftHours = (shift) => {
  if (isNumber(shift?.hours)) {
    return shift.hours;
  }
  if (isNumber(shift?.shift_length)) {
    return shift.shift_length;
  }
  if (isNumber(shift?.shift_start) && isNumber(shift?.shift_end)) {
    return shift.shift_end - shift.shift_start;
  }
  return 0;
};

function calculateScheduleTotals(schedule) {
  if (!Array.isArray(schedule)) {
    return { hours: 0, shifts: 0 };
  }

  return schedule.reduce(
    (totals, shift) => {
      if (!shift.other_store) {
        totals.hours += getShiftHours(shift);
      }
      totals.shifts += 1;
      return totals;
    },
    { hours: 0, shifts: 0 }
  );
}

function getRowsForDate(rows, date) {
  return (rows || []).filter((row) => row?.date === date && !row?.other_store);
}

function getShiftBoundaryValues(scheduleRows) {
  const values = [];
  (scheduleRows || []).forEach((shift) => {
    const start = toOptionalNumber(shift?.shift_start);
    const end = toOptionalNumber(shift?.shift_end);
    if (start !== null) values.push(start);
    if (end !== null) values.push(end);
  });
  return values;
}

function buildTimelineRange(currentScheduleRows, proposedScheduleRows) {
  const values = [
    ...getShiftBoundaryValues(currentScheduleRows),
    ...getShiftBoundaryValues(proposedScheduleRows),
  ];
  if (!values.length) {
    return { startHour: undefined, endHour: undefined };
  }
  return {
    startHour: Math.max(0, Math.floor(Math.min(...values))),
    endHour: Math.ceil(Math.max(...values)),
  };
}

function getQuarterHour(row) {
  const parsed = Number(row?.qh_num);
  return Number.isFinite(parsed) ? parsed : null;
}

function getForecastLabor(row) {
  const hours = toOptionalNumber(row?.hours);
  if (hours !== null) {
    return hours * 4;
  }
  return toNumber(row?.labor);
}

function aggregateLaborByQuarterHour(rows, valueAccessor) {
  return (rows || []).reduce((acc, row) => {
    const qhNum = getQuarterHour(row);
    if (qhNum === null) {
      return acc;
    }
    acc[qhNum] = (acc[qhNum] || 0) + valueAccessor(row);
    return acc;
  }, {});
}

function getShiftTimeRange(scheduleRows) {
  const values = [];
  (scheduleRows || []).forEach((shift) => {
    const start = toOptionalNumber(shift?.shift_start);
    const end = toOptionalNumber(shift?.shift_end);
    if (start !== null) values.push(start);
    if (end !== null) values.push(end);
  });
  return values;
}

function getLaborRangeValues(rows, valueAccessor) {
  return (rows || [])
    .filter((row) => valueAccessor(row) > 0)
    .map((row) => getQuarterHour(row))
    .filter((qhNum) => qhNum !== null);
}

function buildCombinedIntradayData({
  date,
  currentLaborRows,
  proposedLaborRows,
  forecastedLaborRows,
  currentScheduleRows,
  proposedScheduleRows,
  formatTime,
}) {
  const currentByQh = aggregateLaborByQuarterHour(
    currentLaborRows,
    (row) => toNumber(row?.labor)
  );
  const proposedByQh = aggregateLaborByQuarterHour(
    proposedLaborRows,
    (row) => toNumber(row?.labor)
  );
  const forecastedByQh = aggregateLaborByQuarterHour(
    forecastedLaborRows,
    getForecastLabor
  );
  const activeQhValues = [
    ...getLaborRangeValues(currentLaborRows, (row) => toNumber(row?.labor)),
    ...getLaborRangeValues(proposedLaborRows, (row) => toNumber(row?.labor)),
    ...getLaborRangeValues(forecastedLaborRows, getForecastLabor),
    ...getShiftTimeRange(currentScheduleRows),
    ...getShiftTimeRange(proposedScheduleRows),
  ];

  if (!date || !activeQhValues.length) {
    return [];
  }

  const startQh = Math.max(0, Math.floor(Math.min(...activeQhValues) * 4));
  const endQh = Math.ceil(Math.max(...activeQhValues) * 4);
  const rows = [];
  for (let qh = startQh; qh <= endQh; qh += 1) {
    const qhNum = qh / 4;
    rows.push({
      qh_num: qhNum,
      time: formatTime(qhNum),
      forecasted: forecastedByQh[qhNum] || 0,
      current: currentByQh[qhNum] || 0,
      recommended: proposedByQh[qhNum] || 0,
    });
  }
  return rows;
}

function formatScore(value) {
  const parsed = toOptionalNumber(value);
  return parsed === null ? "-" : Math.round(parsed);
}

function formatScoreDelta(value) {
  const parsed = toOptionalNumber(value);
  if (parsed === null) {
    return "-";
  }
  const prefix = parsed > 0 ? "+" : "";
  return `${prefix}${Number(parsed.toFixed(1))}`;
}

function getDeltaToneClass(delta) {
  const parsed = toOptionalNumber(delta);
  if (parsed === null || parsed === 0) {
    return "bg-slate-100 text-slate-600";
  }
  return parsed > 0
    ? "bg-emerald-50 text-emerald-700"
    : "bg-amber-50 text-amber-700";
}

function buildWeekDays(weekStartDate, schedDif, schedDifByDate, changedRows) {
  const weekDates = weekStartDate
    ? Array.from({ length: 7 }, (_, index) => {
        const date = addLocalDays(weekStartDate, index);
        return date ? formatDate(date) : null;
      }).filter(Boolean)
    : [...(schedDif || []), ...changedRows].map((day) => day.date).filter(Boolean).sort();

  const uniqueDates = [...new Set(weekDates)];
  return uniqueDates.map((date) => {
    const changedDay = schedDifByDate[date];
    return {
      ...(changedDay || {}),
      date,
      diffs: changedDay?.diffs || [],
      hasChanges: changedRows.some((shift) => shift.date === date),
    };
  });
}

function buildGradeComparison({
  currentScore,
  proposedScore,
  calcLetter,
  scoreToColor,
}) {
  const normalizedCurrentScore = toOptionalNumber(currentScore);
  const normalizedProposedScore = toOptionalNumber(proposedScore);
  const currentLetter =
    normalizedCurrentScore === null ? "-" : calcLetter(normalizedCurrentScore);
  const proposedLetter =
    normalizedProposedScore === null ? "-" : calcLetter(normalizedProposedScore);
  return {
    currentScore: normalizedCurrentScore,
    proposedScore: normalizedProposedScore,
    currentLetter,
    proposedLetter,
    currentColor: scoreToColor(normalizedCurrentScore, currentLetter),
    proposedColor: scoreToColor(normalizedProposedScore, proposedLetter),
    delta:
      normalizedCurrentScore !== null && normalizedProposedScore !== null
        ? normalizedProposedScore - normalizedCurrentScore
        : null,
  };
}

function getIntradayYAxisMax(data) {
  const maxValue = (data || []).reduce(
    (max, row) =>
      Math.max(
        max,
        toNumber(row?.forecasted),
        toNumber(row?.current),
        toNumber(row?.recommended)
      ),
    0
  );
  return Math.max(4, Math.ceil(maxValue + 1));
}

function formatCompactHour(value) {
  const parsed = toOptionalNumber(value);
  if (parsed === null) {
    return "";
  }
  const hour = Math.round(parsed);
  const normalizedHour = ((hour % 24) + 24) % 24;
  const hour12 = normalizedHour % 12 || 12;
  return `${hour12}${normalizedHour < 12 ? "a" : "p"}`;
}

function formatTooltipTime(value) {
  const parsed = toOptionalNumber(value);
  if (parsed === null) {
    return "";
  }
  const totalMinutes = Math.round(parsed * 60);
  const normalizedMinutes = ((totalMinutes % 1440) + 1440) % 1440;
  const hour = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;
  const hour12 = hour % 12 || 12;
  const suffix = hour < 12 ? "am" : "pm";
  return `${hour12}:${String(minutes).padStart(2, "0")}${suffix}`;
}

function buildIntradayTicks(data) {
  const qhValues = (data || [])
    .map((row) => toOptionalNumber(row?.qh_num))
    .filter((value) => value !== null);
  if (!qhValues.length) {
    return [];
  }
  const minHour = Math.min(...qhValues);
  const maxHour = Math.max(...qhValues);
  const tickStep = maxHour - minHour > 10 ? 3 : 2;
  const firstTick = Math.ceil(minHour / tickStep) * tickStep;
  const ticks = [];
  for (let hour = firstTick; hour <= maxHour; hour += tickStep) {
    ticks.push(hour);
  }
  return ticks;
}

function LaborBalanceSummary({ gradeLetter, gradeColor, scheduledHours, forecastHours }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold text-white"
        style={{ backgroundColor: gradeColor }}>{gradeLetter}</span>
      <span className="text-xs text-slate-600">
        <strong className="font-semibold text-slate-700">{toNumber(scheduledHours).toFixed(1)} hrs</strong>
        {" / "}{toNumber(forecastHours).toFixed(1)} forecast
      </span>
    </div>
  );
}

function ScheduleIntradayChart({
  data,
  title,
  gradeLetter,
  gradeColor,
  scheduledHours,
  forecastHours,
  scheduleDataKey,
  scheduleLabel,
  scheduleStroke,
  yAxisMax,
}) {
  const hasData = data.length > 0;
  const xAxisTicks = React.useMemo(() => buildIntradayTicks(data), [data]);

  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) {
      return null;
    }
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-xl">
        <div className="mb-2 text-xs font-semibold text-slate-900">
          {formatTooltipTime(label)}
        </div>
        {payload.map((entry) => (
          <div
            key={entry.dataKey}
            className="flex items-center justify-between gap-6 py-1 text-xs"
          >
            <div className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: entry.stroke }}
              />
              <span className="text-slate-600">{entry.name}</span>
            </div>
            <span className="font-semibold text-slate-900">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        <LaborBalanceSummary gradeLetter={gradeLetter} gradeColor={gradeColor}
          scheduledHours={scheduledHours} forecastHours={forecastHours} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
          Forecasted labor
        </div>
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: scheduleStroke }}
          />
          {scheduleLabel} scheduled
        </div>
      </div>
      <div className="mt-1 h-[160px]">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
            >
              <CartesianGrid stroke="#e2e8f0" />
              <XAxis
                dataKey="qh_num"
                type="number"
                domain={["dataMin", "dataMax"]}
                ticks={xAxisTicks}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatCompactHour}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickLine={false}
                width={28}
                allowDecimals={false}
                domain={[0, yAxisMax]}
              />
              <Tooltip content={renderTooltip} />
              <Area
                type="stepAfter"
                dataKey="forecasted"
                name="Forecasted"
                stroke="#cbd5e1"
                fill="#cbd5e1"
                fillOpacity={1}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="stepAfter"
                dataKey={scheduleDataKey}
                name={scheduleLabel}
                stroke={scheduleStroke}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            No intraday labor data available.
          </div>
        )}
      </div>
    </div>
  );
}
function shortfallColor(shortfall, priority) {
  if (!(shortfall > 0)) return "text-slate-500";
  return priority ? "text-red-700" : "text-amber-700";
}

function currentEmployeeHours(currentSchedule, weekStart) {
  const weekEnd = weekStart ? formatDate(addLocalDays(weekStart, 7)) : null;
  return currentSchedule.reduce((totals, shift) => {
    const memberId = getMemberId(shift);
    if (memberId == null || Number(memberId) < 0 || shift.other_store || shift.status === "AVAILABLE" ||
        (weekStart && (shift.date < weekStart || shift.date >= weekEnd))) return totals;
    const start = toOptionalNumber(shift.shift_start);
    const end = toOptionalNumber(shift.shift_end);
    // Match the proposed target report: subtract unpaid meals, retain paid rests.
    const paid = start !== null && end !== null
      ? Math.max(0, end - start - toNumber(shift.break_length) - toNumber(shift.break_length_2))
      : getShiftHours(shift);
    totals[memberId] = (totals[memberId] || 0) + paid;
    return totals;
  }, {});
}

function EmployeeHoursComparison({ employees, currentHours }) {
  const hours = (value) => value === null || value === undefined ? "—" : `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs`;
  return (
    <section aria-label="Employee target hours" className="overflow-hidden rounded-lg border border-slate-200">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-semibold text-slate-900">Employee targets</h3>
          <MuiTooltip title="Current and proposed paid hours for this week. Shortfall is the weekly target minus scheduled hours, never below zero. Accepting only some days may change proposed totals." arrow describeChild enterTouchDelay={0}>
            <IconButton size="small" aria-label="About employee target hours" sx={{ color: "#64748b" }}>
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </MuiTooltip>
        </div>
        <span className="text-sm text-slate-500">{employees.length} {employees.length === 1 ? "employee" : "employees"}</span>
      </div>
      <div className="overflow-x-auto" role="region" aria-label="Employee target results" tabIndex={0}>
        <table className="w-full min-w-[880px] border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-slate-500">
            <tr>
              {["Employee", "Weekly target", "Current hours", "Current shortfall", "Proposed hours", "Proposed shortfall", "Priority"].map((label, index) => (
                <th key={label} scope="col" className={`whitespace-nowrap border-b border-slate-200 px-4 py-3 font-medium ${index === 0 ? "text-left" : "text-right"}`}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.map((employee) => (
              <tr key={employee.store_team_member_id} className="group hover:bg-slate-50">
                <th scope="row" className="border-b border-slate-100 px-4 py-3 text-left font-medium text-slate-900 group-last:border-0">{employee.name || `Employee ${employee.store_team_member_id}`}</th>
                <td className="whitespace-nowrap border-b border-slate-100 px-4 py-3 text-right tabular-nums text-slate-600 group-last:border-0">{hours(employee.requested_hours)}</td>
                <td className="whitespace-nowrap border-b border-slate-100 px-4 py-3 text-right tabular-nums text-slate-600 group-last:border-0">{hours(currentHours[employee.store_team_member_id] || 0)}</td>
                <td className={`whitespace-nowrap border-b border-slate-100 px-4 py-3 text-right tabular-nums group-last:border-0 ${shortfallColor(employee.requested_hours - (currentHours[employee.store_team_member_id] || 0), employee.prioritize_target_hours)}`}>{hours(Math.max(0, employee.requested_hours - (currentHours[employee.store_team_member_id] || 0)))}</td>
                <td className="whitespace-nowrap border-b border-slate-100 px-4 py-3 text-right tabular-nums text-slate-900 group-last:border-0">{hours(employee.achieved_hours)}</td>
                <td className="whitespace-nowrap border-b border-slate-100 px-4 py-3 text-right group-last:border-0">
                  <span className={`inline-flex items-center gap-1 tabular-nums ${shortfallColor(employee.shortfall_hours, employee.prioritize_target_hours)}`}>
                    {hours(employee.shortfall_hours)}
                    {employee.reasons?.length > 0 && <MuiTooltip title={employee.reasons.map((reason) => reason.message).join(" ")} arrow describeChild enterTouchDelay={0}>
                      <IconButton size="small" aria-label={`Target details for ${employee.name || `Employee ${employee.store_team_member_id}`}`} sx={{ color: "inherit" }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </MuiTooltip>}
                  </span>
                </td>
                <td className="border-b border-slate-100 px-4 py-3 text-right group-last:border-0">
                  {employee.prioritize_target_hours ? <span className="rounded-md bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700">On</span> : <span className="text-slate-500">Off</span>}
                </td>
              </tr>
            ))}
            {employees.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No employee target results for this run.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function AutoScheduleDiffs(props) {
  const [comparisonView, setComparisonView] = React.useState("daily");
  const isWeekly = comparisonView === "weekly";
  const [changedOnly, setChangedOnly] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState(null);

  const schedDif = React.useMemo(
    () => props.sched_dif || [],
    [props.sched_dif]
  );
  const schedDifByDate = React.useMemo(
    () =>
      schedDif.reduce((acc, day) => {
        if (day?.date) {
          acc[day.date] = day;
        }
        return acc;
      }, {}),
    [schedDif]
  );
  const actualSchedule = props.actual_schedule || [];
  const proposedSchedule = props.schedule_differences?.new_schedule || [];
  const changedCurrent = changedScheduleRows(actualSchedule, proposedSchedule);
  const changedProposed = changedScheduleRows(proposedSchedule, actualSchedule);
  const weekDays = buildWeekDays(
    props.week_start_date, schedDif, schedDifByDate, [...changedCurrent, ...changedProposed]
  );
  const firstChangedDayIndex = Math.max(0, weekDays.findIndex((day) => day.hasChanges));
  const selectedDayIndex = selectedDate
    ? weekDays.findIndex((day) => day.date === selectedDate)
    : -1;
  const currentDayIndex =
    selectedDayIndex >= 0 ? selectedDayIndex : firstChangedDayIndex;
  const currentDay = weekDays[currentDayIndex] || weekDays[0];
  const selectedGradeWeights = Array.isArray(props.grade_weights)
    ? props.grade_weights?.[0]
    : props.grade_weights;

  const goToPreviousDay = () => {
    if (currentDayIndex > 0) {
      setSelectedDate(weekDays[currentDayIndex - 1]?.date || null);
      setComparisonView("daily");
    }
  };

  const goToNextDay = () => {
    if (currentDayIndex < weekDays.length - 1) {
      setSelectedDate(weekDays[currentDayIndex + 1]?.date || null);
      setComparisonView("daily");
    }
  };

  const handleDateClick = (index) => {
    setSelectedDate(weekDays[index]?.date || null);
    setComparisonView("daily");
  };

  const visibleCurrent = changedOnly ? changedCurrent : actualSchedule;
  const visibleProposed = changedOnly ? changedProposed : proposedSchedule;
  const comparisonMembers = (props.store_team_member_weekly_info || []).filter((member) => !changedOnly
    || [...changedCurrent, ...changedProposed].some((shift) => getMemberId(shift) === member.id));
  const compliance = props.schedule_differences?.compliance_comparison;
  const availability = props.schedule_differences?.availability_comparison;
  const showAvailability = availability && (availability.before !== 0 || availability.after !== 0);
  const availabilityTone = availability?.before != null && availability?.after != null
    ? availability.after < availability.before ? "text-emerald-700"
      : availability.after > availability.before ? "text-rose-700" : "text-slate-900"
    : "text-slate-900";
  const employeeHours = props.schedule_differences?.employee_hours || [];
  const currentHours = currentEmployeeHours(actualSchedule, props.week_start_date);
  const priorityEmployees = employeeHours.filter((employee) => employee.prioritize_target_hours);
  const currentPriorityShortfalls = priorityEmployees.filter((employee) =>
    employee.requested_hours - (currentHours[employee.store_team_member_id] || 0) > 0).length;
  const proposedPriorityShortfalls = priorityEmployees.filter((employee) => employee.shortfall_hours > 0).length;
  const priorityShortfallTone = proposedPriorityShortfalls < currentPriorityShortfalls ? "text-emerald-700"
    : proposedPriorityShortfalls > currentPriorityShortfalls ? "text-rose-700" : "text-slate-900";
  const actualScheduleLabor = props.actual_schedule_labor || [];
  const proposedScheduleLabor =
    props.schedule_differences?.new_schedule_labor || [];
  const forecastedLabor = props.forecasted_labor || [];
  const formatTime = (qhNum) => (props.get_t_str ? props.get_t_str(qhNum) : qhNum);

  // The optimizer score also weights preferences and constraints. Display only
  // the same Budget + Coverage grade used by the daily comparison cards.
  const weeklyScore = (labor) => forecastedLabor.length
    ? calculateScheduleGradeScore(labor.filter((row) => !row.other_store), forecastedLabor.filter((row) => !row.other_store), selectedGradeWeights)
    : null;
  const weeklyGradeComparison = buildGradeComparison({
    currentScore: weeklyScore(actualScheduleLabor),
    proposedScore: weeklyScore(proposedScheduleLabor),
    calcLetter: calcScheduleLetter,
    scoreToColor: (score) => getScheduleGradeColor(score),
  });
  const buildDailyGradeComparison = (date) => {
    const currentLabor = getRowsForDate(actualScheduleLabor, date);
    const proposedLabor = getRowsForDate(proposedScheduleLabor, date);
    const targetLabor = getRowsForDate(forecastedLabor, date);
    const currentScore = calculateScheduleGradeScore(
      currentLabor,
      targetLabor,
      selectedGradeWeights
    );
    const proposedScore = calculateScheduleGradeScore(
      proposedLabor,
      targetLabor,
      selectedGradeWeights
    );
    return buildGradeComparison({
      currentScore,
      proposedScore,
      calcLetter: calcScheduleLetter,
      scoreToColor: (score) => getScheduleGradeColor(score),
    });
  };

  const dailyScorecardSummaries = weekDays.map((day) => ({
    day, hasChanges: day.hasChanges, gradeComparison: buildDailyGradeComparison(day.date),
  }));
  const selectedDayScorecardSummary =
    dailyScorecardSummaries[currentDayIndex] || dailyScorecardSummaries[0];
  const dailyGradeComparison =
    selectedDayScorecardSummary?.gradeComparison || weeklyGradeComparison;
  const removedCurrent = removedScheduleRows(actualSchedule, proposedSchedule);

  const newScheduleTotals = calculateScheduleTotals(proposedSchedule);
  const oldScheduleTotals = calculateScheduleTotals(actualSchedule);
  const weeklyForecastHours = forecastedLabor.reduce(
    (total, row) => total + toNumber(row?.hours),
    0
  );

  const renderDayStrip = () => {
    if (!weekDays.length) {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        <button
          className={`hidden lg:flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${
            currentDayIndex === 0
              ? "border-slate-200 bg-slate-100 text-slate-300"
              : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
          }`}
          aria-label="Previous day"
          onClick={goToPreviousDay}
          disabled={currentDayIndex === 0}
        >
          <ChevronLeftIcon style={{ fontSize: "22px" }} />
        </button>
        <div
          className="grid min-w-0 flex-1 gap-px overflow-x-auto rounded-lg border border-slate-200 bg-slate-200"
          style={{
            gridTemplateColumns: `repeat(${weekDays.length}, minmax(180px, 1fr))`,
          }}
        >
          {dailyScorecardSummaries.map(({ day, gradeComparison }, index) => {
            const active = currentDayIndex === index && comparisonView === "daily";
            const changedCount = Math.max(getRowsForDate(changedCurrent, day.date).length, getRowsForDate(changedProposed, day.date).length);
            const hasDailyWarnings = compliance?.enabled &&
              (compliance.before?.by_date?.[day.date] !== 0 || compliance.after?.by_date?.[day.date] !== 0);
            return (
              <button key={day.date}
                className={`min-w-0 border-t-2 px-4 py-3 text-left transition-colors focus-visible:outline-violet-600 focus-visible:outline-offset-[-2px] ${
                  active ? "border-violet-600 bg-violet-50" : "border-transparent bg-white hover:bg-slate-50"
                }`}
                aria-label={formatDateReadable(day.date)} title={`${changedCount} changed shifts`}
                aria-pressed={active} onClick={() => handleDateClick(index)}>
                <div className="whitespace-nowrap text-sm font-semibold text-slate-900">
                  {format(parseISO(day.date), "EEE, MMM d")}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-base font-semibold tabular-nums text-slate-900">{formatScore(gradeComparison.currentScore)}</span>
                  <ArrowForwardIcon className="text-slate-400" style={{ fontSize: "16px" }} />
                  <span className="text-base font-semibold tabular-nums text-slate-900">{formatScore(gradeComparison.proposedScore)}</span>
                  <span className={`ml-auto rounded-full px-2.5 py-1 text-xs font-semibold tabular-nums ${getDeltaToneClass(gradeComparison.delta)}`}>
                    {formatScoreDelta(gradeComparison.delta)}
                  </span>
                </div>
                {hasDailyWarnings && <div className="mt-1"><ComplianceCount comparison={compliance} date={day.date} label="Warnings" /></div>}
              </button>
            );
          })}
        </div>
        <button
          className={`hidden lg:flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${
            currentDayIndex === weekDays.length - 1
              ? "border-slate-200 bg-slate-100 text-slate-300"
              : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
          }`}
          aria-label="Next day"
          onClick={goToNextDay}
          disabled={currentDayIndex === weekDays.length - 1}
        >
          <ChevronRightIcon style={{ fontSize: "22px" }} />
        </button>
      </div>
    );
  };

  const renderShiftFilter = () => {
    const rows = (schedule) => isWeekly ? schedule : getRowsForDate(schedule, currentDay?.date);
    return <div className="mb-3 flex flex-wrap items-center justify-between gap-3" role="group" aria-label="Shift display">
      <h3 className={isWeekly ? "text-base font-bold text-slate-900" : "text-sm font-semibold text-slate-900"}>
        {isWeekly ? "Weekly Comparison" : "Shifts"}
      </h3>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs text-slate-500" role="status">
          {changedOnly ? "Changed shifts" : "All shifts"} · {rows(visibleCurrent).length} current / {rows(visibleProposed).length} proposed
        </span>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" className="h-4 w-4 accent-violet-700" checked={changedOnly}
            onChange={(event) => setChangedOnly(event.target.checked)} />
          Changed shifts only
        </label>
      </div>
      {changedOnly && !rows(changedCurrent).length && !rows(changedProposed).length
        && <p className="w-full text-sm text-slate-500">No changed shifts in this view.</p>}
    </div>;
  };

  const renderDailyComparison = () => {
    if (!currentDay) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          No daily schedule changes to compare.
        </div>
      );
    }

    const currentScheduleRows = getRowsForDate(actualSchedule, currentDay.date);
    const proposedScheduleRows = getRowsForDate(proposedSchedule, currentDay.date);
    const currentLaborRows = getRowsForDate(actualScheduleLabor, currentDay.date);
    const proposedLaborRows = getRowsForDate(
      proposedScheduleLabor,
      currentDay.date
    );
    const forecastedRows = getRowsForDate(forecastedLabor, currentDay.date);
    const timelineRange = buildTimelineRange(
      currentScheduleRows,
      proposedScheduleRows
    );
    const intradayData = buildCombinedIntradayData({
      date: currentDay.date,
      currentLaborRows,
      proposedLaborRows,
      forecastedLaborRows: forecastedRows,
      currentScheduleRows,
      proposedScheduleRows,
      formatTime,
    });
    const intradayYAxisMax = getIntradayYAxisMax(intradayData);
    const currentHours = currentLaborRows.reduce(
      (total, row) => total + toNumber(row?.hours),
      0
    );
    const proposedHours = proposedLaborRows.reduce(
      (total, row) => total + toNumber(row?.hours),
      0
    );
    const forecastHours = forecastedRows.reduce(
      (total, row) => total + toNumber(row?.hours),
      0
    );

    return (
      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
            <h3 className="text-base font-bold text-slate-900">
              Daily Comparison
            </h3>
            <span className="text-sm font-semibold text-slate-500">
              {formatDateReadable(currentDay.date)}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-3 md:grid-cols-1">
          <div className="min-w-0">
            <ScheduleIntradayChart
              data={intradayData}
              title="Current Schedule"
              gradeLetter={dailyGradeComparison.currentLetter}
              gradeColor={dailyGradeComparison.currentColor}
              scheduledHours={currentHours}
              forecastHours={forecastHours}
              scheduleDataKey="current"
              scheduleLabel="Current"
              scheduleStroke={PRIMARY_LABOR_COLOR}
              yAxisMax={intradayYAxisMax}
            />
          </div>
          <div className="min-w-0">
            <ScheduleIntradayChart
              data={intradayData}
              title="Proposed Schedule"
              gradeLetter={dailyGradeComparison.proposedLetter}
              gradeColor={dailyGradeComparison.proposedColor}
              scheduledHours={proposedHours}
              forecastHours={forecastHours}
              scheduleDataKey="recommended"
              scheduleLabel="Proposed"
              scheduleStroke={PRIMARY_LABOR_COLOR}
              yAxisMax={intradayYAxisMax}
            />
          </div>
        </div>
        <section className="mt-3" aria-label="Schedule shifts">
          {renderShiftFilter()}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
            <div className="min-w-0 rounded-lg border border-slate-200 p-3">
              <h4 className="mb-3 text-sm font-semibold text-slate-900">Current</h4>
              <AutoScheduleDailyView
                dateStr={currentDay.date}
                schedule={visibleCurrent}
                scheduleLabor={actualScheduleLabor}
                forecastedLabor={forecastedLabor}
                storeTeamMembers={comparisonMembers}
                roleNames={props.role_names || []}
                gradeWeights={selectedGradeWeights}
                week_start_date={props.week_start_date}
                showChart={false}
                timelineStartHour={timelineRange.startHour}
                timelineEndHour={timelineRange.endHour}
                timelineCompact
                showTimelineAxis={false}
                compact
                embedded
              />
            </div>
            <div className="min-w-0 rounded-lg border border-slate-200 p-3">
              <h4 className="mb-3 text-sm font-semibold text-slate-900">Proposed</h4>
              <AutoScheduleDailyView
                dateStr={currentDay.date}
                schedule={visibleProposed}
                scheduleLabor={proposedScheduleLabor}
                forecastedLabor={forecastedLabor}
                storeTeamMembers={comparisonMembers}
                roleNames={props.role_names || []}
                gradeWeights={selectedGradeWeights}
                week_start_date={props.week_start_date}
                showChart={false}
                previousSchedule={actualSchedule}
                changedShifts={changedProposed}
                timelineStartHour={timelineRange.startHour}
                timelineEndHour={timelineRange.endHour}
                timelineCompact
                showTimelineAxis={false}
                compact
                embedded
              />
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderWeeklyComparison = () => (
    <div>
      {renderShiftFilter()}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-1">
        <div className="min-w-0">
          <AutoScheduleWeeklyView
            schedule={visibleCurrent}
            storeTeamMembers={comparisonMembers}
            week_start_date={props.week_start_date}
            storeNames={props.storeNames}
            store_id={props.store_id}
            roleNames={props.role_names || []}
            changedShifts={removedCurrent}
            highlightLabel="Removed shift"
            headerTitle="Current Schedule"
            headerSubtitle={`${oldScheduleTotals.shifts} shifts`}
            headerContent={
              <LaborBalanceSummary
                gradeLetter={weeklyGradeComparison.currentLetter}
                gradeColor={weeklyGradeComparison.currentColor}
                scheduledHours={oldScheduleTotals.hours}
                forecastHours={weeklyForecastHours}
              />
            }
          />
        </div>
        <div className="min-w-0">
          <AutoScheduleWeeklyView
            schedule={visibleProposed}
            storeTeamMembers={comparisonMembers}
            week_start_date={props.week_start_date}
            storeNames={props.storeNames}
            store_id={props.store_id}
            roleNames={props.role_names || []}
            changedShifts={changedProposed}
            headerTitle="Proposed Schedule"
            headerSubtitle={`${newScheduleTotals.shifts} shifts`}
            headerContent={
              <LaborBalanceSummary
                gradeLetter={weeklyGradeComparison.proposedLetter}
                gradeColor={weeklyGradeComparison.proposedColor}
                scheduledHours={newScheduleTotals.hours}
                forecastHours={weeklyForecastHours}
              />
            }
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <div className="flex flex-col gap-3">
        <div>
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
            <section aria-label="Weekly summary" className="flex min-w-0 flex-wrap items-start gap-x-6 gap-y-3 py-2">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-slate-900">Weekly score</span>
                <span className="text-lg font-semibold tabular-nums text-slate-900">
                  {formatScore(weeklyGradeComparison.currentScore)} → {formatScore(weeklyGradeComparison.proposedScore)}
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold tabular-nums ${getDeltaToneClass(weeklyGradeComparison.delta)}`}>
                  {formatScoreDelta(weeklyGradeComparison.delta)} points
                </span>
              </div>
              {compliance?.enabled && <div className="min-w-0 border-l border-slate-300 pl-6 sm:w-full sm:border-l-0 sm:pl-0">
                <ScheduleComplianceComparison comparison={compliance} metric />
              </div>}
              {showAvailability && <div className="min-w-0 border-l border-slate-300 pl-6 sm:w-full sm:border-l-0 sm:pl-0">
                <ComparisonMetric label="Availability conflicts" before={availability.before ?? "Unavailable"}
                  after={availability.after ?? "Unavailable"} tone={availabilityTone} />
              </div>}
              {(currentPriorityShortfalls > 0 || proposedPriorityShortfalls > 0) && (
                <div className="min-w-0 border-l border-slate-300 pl-6 sm:w-full sm:border-l-0 sm:pl-0">
                  <ComparisonMetric label="Priority shortfalls" before={currentPriorityShortfalls}
                    after={proposedPriorityShortfalls} tone={priorityShortfallTone} />
                </div>
              )}
            </section>
            <div className="ml-auto flex shrink-0 overflow-hidden rounded-lg bg-slate-100" role="group" aria-label="Comparison view">
              {[["daily", "Daily view"], ["weekly", "Weekly table"], ["employees", "Employee view"]].map(([value, label]) => (
                <button key={value} type="button"
                  className={`px-5 py-2.5 text-sm font-medium transition-colors focus-visible:outline-violet-600 focus-visible:outline-offset-[-2px] ${
                    comparisonView === value ? "bg-violet-700 text-white" : "text-slate-600 hover:text-slate-900"
                  }`} aria-pressed={comparisonView === value} onClick={() => setComparisonView(value)}>{label}</button>
              ))}
            </div>
          </div>
        </div>
        <ExplicitRoleCoverage coverage={props.schedule_differences?.explicit_role_coverage} roleNames={props.role_names || []} />
        {renderDayStrip()}
        {comparisonView === "employees" ? (
          <EmployeeHoursComparison employees={employeeHours} currentHours={currentHours} />
        ) : (
          <div className="min-w-0">
            {isWeekly ? renderWeeklyComparison() : renderDailyComparison()}
          </div>
        )}
      </div>
    </div>
  );
}
